import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtom } from "jotai";

import useWorkspacePermissions from "../../../hooks/useWorkspacePermissions";
import { handleWorkspacesUserUpdate } from "../../../utils/WorkspaceUtils";
import useCloudPermissions from "../../../hooks/useCloudPermissions";
import { callFunctionIfExists } from "../../../utils/GeneralUtils";
import { getErrorMessage } from "../../../utils/GeneralUtils";
import useTenantUsers from "../tenantUsers/useTenantUsers";
import { Person } from "../../../types/GlobalInterfaces";
import { tenantAtom } from "../../../atoms/GlobalAtoms";
import TenantAPI from "../../../api/api/TenantAPI";
import Constants from "../../../utils/Constants";
import useUser from "../auth/useUser";

interface InviteUserProps {
  email: string;
  permission: string;
  supportTargetCloudID: string | undefined; // For support requests - ONLY users with correct permissions can use.
  workspaceInfo?: {
    workspaceID: string;
    workspaceProfileID: string;
  };
  defaultWorkspace?: boolean;
  onSuccess?: () => any;
}

// For inviting users to a Cloud, OR inviting users to Workspace AND Cloud
// If workspaceInfo is provided, we are adding the user to workspace AND cloud.
const useInviteUser = () => {
  const queryClient = useQueryClient();
  const { cPermissions } = useCloudPermissions();
  const { wPermissions } = useWorkspacePermissions();
  const [tenant] = useAtom(tenantAtom);
  const { users } = useTenantUsers();
  const { wPermissions: workspacePermissions } = useUser();

  const inviteUser = useMutation({
    mutationFn: ({
      email,
      permission,
      supportTargetCloudID,
      workspaceInfo,
    }: InviteUserProps) => {
      // Cloud Permissions must be checked, if this IS NOT a support user request.
      if (!supportTargetCloudID && !cPermissions.cloud_create_person) {
        return Promise.reject("User does not have permission to invite users");
      }

      if (workspaceInfo && !wPermissions.workspace_add_user) {
        return Promise.reject(
          "User does not have permission to add users to this workspace"
        );
      }

      return TenantAPI.inviteUser({
        userEmail: email,
        permissionProfile: permission,
        supportTargetCloudID: supportTargetCloudID,
        workspaceInfo: workspaceInfo,
      });
    },
    onSuccess: (data, { workspaceInfo, onSuccess, supportTargetCloudID }) => {
      // Returns { user, workspace }. Workspace used in handleWorkspacesUserUpdate
      const { user: newUser } = data.data.data || {};

      queryClient.setQueryData<Person[]>(
        ["tenantUsers", tenant?.TenantID],
        (users) => {
          if (!Array.isArray(users)) return users;

          return [...users, newUser];
        }
      );

      queryClient.setQueryData<Person[]>(["people"], (users) => {
        if (!Array.isArray(users)) return users;

        return [...users, newUser];
      });

      if (supportTargetCloudID) {
        queryClient.invalidateQueries({
          queryKey: [Constants.SUP_CLOUD_ID, "People", "records"],
        });
      }

      queryClient.setQueryData(["person", newUser?.id], newUser);
      queryClient.invalidateQueries({
        queryKey: ["person", newUser?.id],
      });

      queryClient.invalidateQueries({
        queryKey: ["cloud", supportTargetCloudID],
      });

      if (workspaceInfo) {
        handleWorkspacesUserUpdate(
          data,
          queryClient,
          workspaceInfo.workspaceID,
          workspacePermissions,
          users,
          tenant?.TenantID ?? ""
        );
      }

      callFunctionIfExists(onSuccess);
    },
  });

  return {
    inviteUser: inviteUser.mutate,
    invitingUser: inviteUser.isPending,
    inviteUserError: inviteUser.error
      ? getErrorMessage(inviteUser.error)
      : null,
    clearError: inviteUser.reset,
  };
};

export default useInviteUser;
